import { supabase } from '@/integrations/supabase/client';

// Create Stripe customer using real Stripe API
export const createStripeCustomer = async (email: string, name: string) => {
  try {
    console.log('Creating real Stripe customer for:', email);

    const STRIPE_SECRET_KEY = import.meta.env.VITE_STRIPE_SECRET_KEY;
    if (!STRIPE_SECRET_KEY) {
      throw new Error('Stripe secret key not configured');
    }

    // Import and initialize Stripe
    const { default: Stripe } = await import('stripe');
    const stripe = new Stripe(STRIPE_SECRET_KEY, {
      apiVersion: '2023-10-16',
    });

    // Create real Stripe customer
    const customer = await stripe.customers.create({
      email: email,
      name: name,
      metadata: {
        source: 'offerchats_admin',
        created_via: 'admin_panel'
      }
    });

    console.log('Real Stripe customer created:', customer.id);

    // Update the user profile with the real Stripe customer ID
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        stripe_customer_id: customer.id,
        updated_at: new Date().toISOString()
      })
      .eq('email', email);

    if (updateError) {
      console.error('Error updating user profile with Stripe customer ID:', updateError);
      throw updateError;
    }

    const result = {
      customer_id: customer.id,
      email: customer.email,
      name: customer.name,
      created: customer.created,
      message: 'Real Stripe customer created successfully'
    };

    console.log('Stripe customer created and profile updated:', result);
    return result;
  } catch (error) {
    console.error('Failed to create Stripe customer:', error);
    throw error;
  }
};

// Stripe configuration
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const STRIPE_SECRET_KEY = import.meta.env.VITE_STRIPE_SECRET_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.warn('VITE_STRIPE_PUBLISHABLE_KEY is not set');
}

// Initialize Stripe (client-side)
let stripe: any = null;

export const initializeStripe = async () => {
  if (!stripe && STRIPE_PUBLISHABLE_KEY) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripe;
};

// Create checkout session using direct Stripe API calls
export const createCheckoutSession = async (planId: string) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Get plan details
    const { data: plan, error: planError } = await supabase
      .from('plans')
      .select('*')
      .eq('id', planId)
      .eq('is_active', true)
      .single();

    if (planError || !plan) {
      throw new Error('Plan not found or inactive');
    }

    if (!plan.stripe_price_id) {
      throw new Error('Plan not configured for billing');
    }

    // Get or create user profile
    let { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('stripe_customer_id')
      .eq('id', user.id)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      throw profileError;
    }

    // If no profile exists, create one
    if (!profile) {
      const { data: newProfile, error: createError } = await supabase
        .from('user_profiles')
        .insert([{
          id: user.id,
          email: user.email,
          name: user.user_metadata?.name || user.email,
        }])
        .select('stripe_customer_id')
        .single();

      if (createError) {
        throw createError;
      }
      profile = newProfile;
    }

    // Create checkout session using Stripe's client-side API
    const stripeInstance = await initializeStripe();
    if (!stripeInstance) {
      throw new Error('Failed to initialize Stripe');
    }

    // Create checkout session data
    const checkoutData = {
      mode: 'subscription' as const,
      line_items: [{
        price: plan.stripe_price_id,
        quantity: 1,
      }],
      success_url: import.meta.env.VITE_STRIPE_SUCCESS_URL || `${window.location.origin}/portal/billing?success=true`,
      cancel_url: import.meta.env.VITE_STRIPE_CANCEL_URL || `${window.location.origin}/portal/billing?canceled=true`,
      customer_email: user.email,
      metadata: {
        user_id: user.id,
        plan_id: planId,
      },
    };

    // Check if user already has a subscription for upgrade/downgrade logic
    let isUpgrade = false;
    let isDowngrade = false;

    if (profile?.plan_id) {
      const { data: currentPlan } = await supabase
        .from('plans')
        .select('price')
        .eq('id', profile.plan_id)
        .single();

      if (currentPlan) {
        isUpgrade = plan.price > currentPlan.price;
        isDowngrade = plan.price < currentPlan.price;
      }
    }

    // Create actual Stripe checkout session using server-side API simulation
    try {
      // For E2E testing, we'll create a real Stripe checkout session
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: plan.stripe_price_id,
          userId: user.id,
          planId: planId,
          customerEmail: user.email,
          successUrl: checkoutData.success_url,
          cancelUrl: checkoutData.cancel_url,
        }),
      });

      if (response.ok) {
        const sessionData = await response.json();
        return {
          planId,
          priceId: plan.stripe_price_id,
          planName: plan.name,
          price: plan.price,
          sessionId: sessionData.sessionId,
          url: sessionData.url,
          isUpgrade,
          isDowngrade,
          mode: profile?.stripe_customer_id ? 'subscription_update' : 'subscription',
        };
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.log('Server-side checkout not available, using client-side simulation');

      // Fallback: Use Stripe.js redirectToCheckout for testing
      const sessionData = await stripeInstance.redirectToCheckout({
        lineItems: checkoutData.line_items,
        mode: checkoutData.mode,
        successUrl: checkoutData.success_url,
        cancelUrl: checkoutData.cancel_url,
        customerEmail: checkoutData.customer_email,
      });

      if (sessionData.error) {
        throw new Error(sessionData.error.message);
      }

      // This won't return normally as redirectToCheckout redirects the page
      return {
        planId,
        priceId: plan.stripe_price_id,
        planName: plan.name,
        price: plan.price,
        sessionId: 'redirect_initiated',
        url: 'redirecting...',
        isUpgrade,
        isDowngrade,
        mode: profile?.stripe_customer_id ? 'subscription_update' : 'subscription',
      };
    }

  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

// Create billing portal session
export const createBillingPortalSession = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('stripe_customer_id, subscription_status')
      .eq('id', user.id)
      .single();

    if (!profile?.stripe_customer_id) {
      throw new Error('No billing information found. Please contact support.');
    }

    if (profile.subscription_status === 'free') {
      throw new Error('Free plan users do not have access to billing portal. Please upgrade to a paid plan first.');
    }

    // Create billing portal session
    console.log(`Creating billing portal session for customer: ${profile.stripe_customer_id}`);

    const returnUrl = `${window.location.origin}/portal/billing/plan`;

    // For production, you would typically call your backend API here
    // Example: const response = await fetch('/api/create-billing-portal-session', { ... })

    // For now, we'll use a direct approach with Stripe Customer Portal
    // This requires the customer to have an active subscription

    // Since we can't create billing portal sessions from client-side with publishable key,
    // we'll redirect to a customer portal URL pattern or show instructions

    // Option 1: Direct Stripe customer portal (if configured)
    const customerPortalUrl = `https://billing.stripe.com/p/login/test_${profile.stripe_customer_id}`;

    // Option 2: For production, implement server-side endpoint
    // const response = await fetch('/api/stripe/create-billing-portal', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     customerId: profile.stripe_customer_id,
    //     returnUrl: returnUrl
    //   })
    // });

    return {
      customerId: profile.stripe_customer_id,
      returnUrl: returnUrl,
      url: customerPortalUrl,
      sessionId: `portal_${Date.now()}`,
      message: 'Redirecting to Stripe Customer Portal...',
    };

  } catch (error) {
    console.error('Error creating billing portal session:', error);
    throw error;
  }
};

// Handle subscription webhook events (this would be called from your webhook endpoint)
export const handleStripeWebhook = async (event: any) => {
  try {
    switch (event.type) {
      case 'customer.subscription.created':
      case 'customer.subscription.updated':
        await handleSubscriptionUpdate(event.data.object);
        break;
      case 'customer.subscription.deleted':
        await handleSubscriptionCancellation(event.data.object);
        break;
      case 'invoice.payment_succeeded':
        await handlePaymentSuccess(event.data.object);
        break;
      case 'invoice.payment_failed':
        await handlePaymentFailure(event.data.object);
        break;
      default:
        console.log(`Unhandled event type: ${event.type}`);
    }
  } catch (error) {
    console.error('Error handling webhook:', error);
    throw error;
  }
};

const handleSubscriptionUpdate = async (subscription: any) => {
  const userId = subscription.metadata?.user_id;
  const planId = subscription.metadata?.plan_id;

  if (!userId) return;

  // Update user profile with subscription info
  const { error } = await supabase
    .from('user_profiles')
    .update({
      stripe_customer_id: subscription.customer,
      stripe_subscription_id: subscription.id,
      subscription_status: subscription.status,
      plan_id: planId,
      current_period_start: new Date(subscription.current_period_start * 1000).toISOString(),
      current_period_end: new Date(subscription.current_period_end * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);

  if (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }

  // Create or update subscription record
  const { error: subError } = await supabase
    .from('subscriptions')
    .upsert({
      id: subscription.id,
      user_id: userId,
      plan_id: planId,
      status: subscription.status,
      current_period_start: new Date(subscription.current_period_start * 1000).toISOString(),
      current_period_end: new Date(subscription.current_period_end * 1000).toISOString(),
      created_at: new Date(subscription.created * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    });

  if (subError) {
    console.error('Error upserting subscription:', subError);
    throw subError;
  }
};

const handleSubscriptionCancellation = async (subscription: any) => {
  const userId = subscription.metadata?.user_id;
  if (!userId) return;

  // Update user profile
  const { error } = await supabase
    .from('user_profiles')
    .update({
      subscription_status: 'canceled',
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);

  if (error) {
    console.error('Error updating canceled subscription:', error);
    throw error;
  }

  // Update subscription record
  const { error: subError } = await supabase
    .from('subscriptions')
    .update({
      status: 'canceled',
      updated_at: new Date().toISOString(),
    })
    .eq('id', subscription.id);

  if (subError) {
    console.error('Error updating subscription record:', subError);
    throw subError;
  }
};

const handlePaymentSuccess = async (invoice: any) => {
  const customerId = invoice.customer;
  
  // Update user profile payment status
  const { error } = await supabase
    .from('user_profiles')
    .update({
      subscription_status: 'active',
      updated_at: new Date().toISOString(),
    })
    .eq('stripe_customer_id', customerId);

  if (error) {
    console.error('Error updating payment success:', error);
    throw error;
  }
};

const handlePaymentFailure = async (invoice: any) => {
  const customerId = invoice.customer;
  
  // Update user profile payment status
  const { error } = await supabase
    .from('user_profiles')
    .update({
      subscription_status: 'past_due',
      updated_at: new Date().toISOString(),
    })
    .eq('stripe_customer_id', customerId);

  if (error) {
    console.error('Error updating payment failure:', error);
    throw error;
  }
};
